import { useEffect, useMemo, useRef, useState } from 'react'
import { QRCodeCanvas } from 'qrcode.react'
import { onValue, ref, update, remove, get, serverTimestamp } from 'firebase/database'

import { Modal, Field } from './AdminApp.jsx'
import {
  aggregateBuyers,
  clearCalcuttaBid,
  computePayouts,
  computeFlightPayouts,
  finishCalcuttaFlight,
  setAuctionOpen,
  setBuyerPaid,
  setCalcuttaBid,
  setCalcuttaFlightResult,
  setCalcuttaLive,
  setCalcuttaSettings,
} from '../lib/data.js'
import { lastNames } from '../lib/teamName.js'
import { PBCC_PREMIUM } from '../config/siteMode.js'
import { db, tref } from '../lib/firebase.js'
import { CALCUTTA_TV_SCENES, isCalcuttaScreen } from '../leaderboard/tvScenes.js'
import { parseCalcuttaLine, isFinishCommand } from '../lib/calcuttaVoice.js'
import { useSpeech } from '../lib/useSpeech.js'
import { useHideAiAssistant } from '../lib/aiAssistantHide.js'

// TEST = the club's PER-FLIGHT model (each flight pays 1st/2nd from ITS pot,
// after MGA takes 10% off the top). Club/live keep the original tournament-wide
// 50/30/20.
const DEFAULT_PAYOUTS = PBCC_PREMIUM
  ? [{ place: 1, pct: 70 }, { place: 2, pct: 30 }]
  : [{ place: 1, pct: 50 }, { place: 2, pct: 30 }, { place: 3, pct: 20 }]
const DEFAULT_MGA_PCT = PBCC_PREMIUM ? 10 : 0

// All five admin sections live in this one tab so admins don't have to hop
// around. Settings collapses by default once configured.
//
// Bidding flow: admins no longer enter bids — bidders self-bid from
// /calcutta-bid via QR code. This tab just configures, broadcasts the QR,
// shows the live bid board, and tracks payment.
// "Keep Calcutta unlocked" — OFF by default. The home-page Calcutta gate re-locks
// on every exit (code 2026 required each entry). Turning this ON stores a flag
// (localStorage) so the gate is skipped while moving around the site; turning it
// OFF re-locks. Purely local, admin-only; nothing public/TV ever sees it.
function CalcuttaAccessToggle() {
  const [keep, setKeep] = useState(() => { try { return localStorage.getItem('calcutta_keep_unlocked') === '1' } catch { return false } })
  const toggle = () => {
    const next = !keep
    setKeep(next)
    try { if (next) localStorage.setItem('calcutta_keep_unlocked', '1'); else localStorage.removeItem('calcutta_keep_unlocked') } catch { /* ignore */ }
  }
  return (
    <div className="card flex items-center justify-between gap-3 no-print">
      <div className="min-w-0">
        <div className="font-bold text-sm">🔒 Access — Keep Calcutta unlocked</div>
        <div className="text-xs text-gray-500 mt-0.5">
          {keep
            ? 'ON — leave and return without re-entering the code. Turn OFF to re-lock (code 2026 required on every entry).'
            : 'OFF (default) — the 2026 code is required every time you open Calcutta from the home page.'}
        </div>
      </div>
      <button type="button" onClick={toggle}
        className="shrink-0 rounded-full px-3 py-1.5 text-sm font-bold border"
        style={keep ? { background: 'var(--ge-primary)', color: '#fff', borderColor: 'var(--ge-primary)' } : { background: '#fff', color: 'var(--ge-primary)', borderColor: '#d1d5db' }}>
        {keep ? '🔓 Unlocked — tap to re-lock' : '🔒 Locked each entry — tap to keep unlocked'}
      </button>
    </div>
  )
}

// Phone remote link/QR — hand it to the roaming auction clerk. Same auction,
// gated by 2026, entry-only (no TV/settings). Feeds the same calcutta node.
function CalcuttaRemoteCard() {
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const url = `${origin}/calcutta-remote`
  return (
    <div className="card" style={{ border: '2px solid var(--ge-gold)', background: '#FBF8EE' }}>
      <div className="flex items-center gap-5 flex-wrap sm:flex-nowrap">
        <div style={{ background: '#fff', padding: 10, borderRadius: 12, border: '1px solid #E5E0CF' }}>
          <QRCodeCanvas value={url} size={168} marginSize={2} fgColor="#000000" bgColor="#FFFFFF" />
        </div>
        <div className="min-w-0">
          <div style={{ color: 'var(--ge-gold)', fontWeight: 800, letterSpacing: 2, fontSize: 12 }}>📱 AUCTION REMOTE</div>
          <h2 className="text-2xl font-extrabold" style={{ color: 'var(--ge-primary)', lineHeight: 1.1 }}>Scan to open on your phone</h2>
          <p className="text-sm text-gray-600 mt-1 max-w-md">
            Point your phone camera at the code, enter access code <b>2026</b>, and you're in. Record winning bids from anywhere in the room — the PC and TVs update live. Speak or tap. Entry only — TVs &amp; settings stay here on the PC.
          </p>
          <a href={url} target="_blank" rel="noreferrer" className="text-xs text-green-deep font-mono break-all underline mt-1 inline-block no-print">{url}</a>
        </div>
      </div>
    </div>
  )
}

export default function CalcuttaTab({ tournamentId, tournament, flights }) {
  // The Calcutta auction AI lives in this tab (toggles + speak/type), so hide
  // the global floating AI circle while this tab is open — everywhere else keeps it.
  useHideAiAssistant()
  const settings = tournament.calcutta?.settings || {}
  const bids = tournament.calcutta?.bids || {}
  const paidByBuyer = tournament.calcutta?.paid || {}

  const enabled = !!settings.enabled
  const auctionOpen = settings.auctionOpen !== false // default true once enabled
  const buyBackEnabled = !!settings.buyBackEnabled
  const buyBackPct = Number(settings.buyBackPct) || 50
  const payoutStructure = Array.isArray(settings.payoutStructure) && settings.payoutStructure.length > 0
    ? settings.payoutStructure
    : DEFAULT_PAYOUTS

  const allTeams = useMemo(() => {
    const out = []
    for (const f of flights) {
      const teams = Object.entries(f.teams || {})
      for (const [tid, t] of teams) {
        out.push({
          flightId: f.id,
          flightName: f.info?.name || f.id,
          teamId: tid,
          teamName: lastNames(t.info?.name) || tid,
          players: t.info?.players || [],
        })
      }
    }
    return out
  }, [flights])

  const totalPot = useMemo(() => {
    let sum = 0
    for (const b of Object.values(bids)) {
      sum += Number(b.bidAmount) || 0
      if (buyBackEnabled && b.buyBack) sum += Number(b.buyBackCost) || 0
    }
    return sum
  }, [bids, buyBackEnabled])

  const payouts = useMemo(() => computePayouts(totalPot, payoutStructure), [totalPot, payoutStructure])
  const buyers = useMemo(() => aggregateBuyers(bids, paidByBuyer), [bids, paidByBuyer])
  // TEST per-flight model: each flight pays from ITS own pot after MGA's cut.
  const perFlight = PBCC_PREMIUM
  const mgaPct = perFlight ? (Number.isFinite(Number(settings.mgaPct)) ? Number(settings.mgaPct) : DEFAULT_MGA_PCT) : 0
  const flightPayouts = useMemo(
    () => (perFlight ? computeFlightPayouts(flights, bids, { mgaPct, payoutStructure, buyBackEnabled }) : null),
    [perFlight, flights, bids, mgaPct, payoutStructure, buyBackEnabled],
  )

  // Ready-state note when the field isn't flighted yet (shown on the standalone
  // home entry AND the normal Calcutta tab — helpful either way).
  const noFlightsBanner = flights.length === 0 ? (
    <div className="card" style={{ background: '#FFF7E6', border: '1px solid #F0D48A' }}>
      <p className="text-sm text-amber-900"><b>Build the field into a tournament to start the auction</b> — Calcutta populates automatically once flights exist.</p>
    </div>
  ) : null

  if (!enabled) {
    return (
      <>
      {noFlightsBanner}
      <CalcuttaAccessToggle />
      <div className="card text-center py-10">
        <div className="text-3xl mb-2">💰</div>
        <h2 className="text-lg font-bold">Calcutta Auction</h2>
        <p className="text-sm text-gray-600 mt-1 max-w-md mx-auto">
          Run a side-pot auction where bidders place bids on teams from their phones. Top finishers' owners split the pot.
        </p>
        <button
          className="btn-primary mt-4"
          onClick={() => setCalcuttaSettings(tournamentId, {
            enabled: true,
            auctionOpen: true,
            payoutStructure: DEFAULT_PAYOUTS,
            mgaPct: DEFAULT_MGA_PCT,
            ...(PBCC_PREMIUM ? { perFlight: true } : {}),
            buyBackEnabled: true,
            buyBackPct: 50,
          })}>
          Enable Calcutta
        </button>
      </div>
      </>
    )
  }

  return (
    <div className="space-y-4">
      {noFlightsBanner}
      <CalcuttaAccessToggle />
      {/* PRIMARY WAY IN for the roaming clerk — scan, enter 2026, record bids. */}
      <CalcuttaRemoteCard />
      {/* PRIMARY: the auctioneer-run auction. Admin picks a flight, then only
          enters Winning Bid + Owner per team and Saves — which auto-advances and
          drives the public display. */}
      <AuctionRunner
        tournamentId={tournamentId}
        flights={flights}
        bids={bids}
        live={tournament.calcutta?.live || null}
        finished={tournament.calcutta?.finished || {}}
        flightResult={tournament.calcutta?.flightResult || {}}
        mgaPct={mgaPct}
        payoutStructure={payoutStructure}
        buyBackEnabled={buyBackEnabled}
      />
      <CalcuttaTvMonitor tournamentId={tournamentId} />
      <CalcuttaTvControl tournamentId={tournamentId} />
      <SettingsCard
        tournamentId={tournamentId}
        settings={settings}
        payoutStructure={payoutStructure}
      />
      <LiveBidBoardCard
        tournamentId={tournamentId}
        allTeams={allTeams}
        bids={bids}
        buyBackEnabled={buyBackEnabled}
      />
      {perFlight
        ? <FlightPayoutsCard flightPayouts={flightPayouts} mgaPct={mgaPct} totalPot={totalPot} bidCount={Object.keys(bids).length} />
        : <PotSummaryCard totalPot={totalPot} payouts={payouts} bidCount={Object.keys(bids).length} />}
      <PaymentTrackingCard tournamentId={tournamentId} buyers={buyers} />
      <BuyerQRCard tournamentId={tournamentId} buyers={buyers} />
    </div>
  )
}

// Optional emojis for the Calcutta announcement message.
const CALCUTTA_ANN_EMOJIS = ['⛳', '🏌️', '🎙️', '💰', '🏆', '🥇', '🔨', '📢', '⏰', '🍔', '🍻', '🎉', '👏', '🔥', '⚡', '😊', '👍']

// ── Calcutta TV Monitor (clone of the tournament TV Monitor) ────────────────
// Live preview tiles for the six Calcutta screens; click a tile to open it full
// screen. Same layout/behavior as TvMonitorWall, scoped to Calcutta screens.
function CalcuttaTvMonitor({ tournamentId }) {
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  return (
    <div className="card">
      <div className="mb-3 no-print">
        <h2 className="text-lg font-bold">📺 Calcutta TV Monitor</h2>
        <p className="text-sm text-gray-600 mt-0.5">Live preview of every Calcutta screen — click a tile to open it full screen.</p>
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        {CALCUTTA_TV_SCENES.map((sc) => {
          const href = `${origin}/calcutta-live?t=${encodeURIComponent(tournamentId)}&screen=${sc.screen}`
          return (
            <a key={sc.key} href={href} target="_blank" rel="noreferrer"
              className="block rounded-2xl overflow-hidden bg-black border border-gray-200 shadow hover:shadow-xl transition"
              style={{ height: '46vh', minHeight: 320 }}>
              <div className="px-3 py-2 text-xs font-extrabold uppercase tracking-wider flex items-center justify-between" style={{ backgroundColor: 'var(--ge-primary)', color: 'var(--ge-gold)' }}>
                <span>{sc.icon} {sc.label}</span>
                <span className="text-white/70 text-[10px]">↗ Open</span>
              </div>
              <div className="relative" style={{ height: 'calc(100% - 32px)' }}>
                {/* Preview only — the link wrapper handles the click. */}
                <iframe title={sc.label} src={href}
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0, pointerEvents: 'none' }} scrolling="no" />
              </div>
            </a>
          )
        })}
      </div>
    </div>
  )
}

// ── Calcutta TV Screen Control (clone of the tournament TV Screen Control) ──
// Scoped to the "C" pool ONLY. Announcements (C-scoped) + one control block per
// connected "C" TV: name, LIVE badge, Remove, a tab per Calcutta screen + Rotate,
// and a rotate-speed selector. Click a tab → that screen goes up on THAT TV.
// Mirrors TvControlApp's per-TV rows. MGA never shows.
const CAL_SPEEDS = [15, 30, 60]
const CAL_LIVE_WINDOW_MS = 90000 // heartbeat 30s → "live" if seen within 90s
function CalcuttaTvControl({ tournamentId }) {
  const [screens, setScreens] = useState({})
  const [nameDraft, setNameDraft] = useState({})
  const [annText, setAnnText] = useState('')
  const [annTarget, setAnnTarget] = useState('all') // 'all' (all C screens) | screenId
  const [showEmoji, setShowEmoji] = useState(false)
  const [msg, setMsg] = useState('')
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    const off = onValue(tref('tvControl/screens'), (s) => setScreens(s.val() || {}))
    return () => off()
  }, [])
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 5000); return () => clearInterval(id) }, [])
  // Calcutta pool = only "C"-suffixed screens.
  const tvs = Object.entries(screens)
    .filter(([id]) => isCalcuttaScreen(id))
    .sort((a, b) => (String(a[0]).length - String(b[0]).length) || (a[0] < b[0] ? -1 : 1))
  const setMode = (id, m) => update(tref(`tvControl/screens/${id}`), { mode: m, tournamentId }).catch(() => {})
  const setSpeed = (id, s) => update(tref(`tvControl/screens/${id}`), { rotateSeconds: s }).catch(() => {})
  const setName = (id, v) => update(tref(`tvControl/screens/${id}`), { name: v.trim() || null }).catch(() => {})
  const removeScreen = (id, name) => {
    if (!window.confirm(`Remove ${name || `TV ${id}`} from the Calcutta panel?\n\nIf /tv?screen=${id} is still open on that TV it reappears on its next check-in — close it first.`)) return
    remove(tref(`tvControl/screens/${id}`)).catch(() => {})
  }
  // Announcements — scoped to the C pool ONLY (never the global channel, which
  // would also hit tournament TVs). 'all' writes a per-screen announcement to
  // every C screen; a specific target writes just that one. Per-screen wins.
  const annIds = () => (annTarget === 'all' ? tvs.map(([id]) => id) : [annTarget])
  const postAnn = () => {
    const text = annText.trim(); if (!text) return
    const ids = annIds(); if (ids.length === 0) { setMsg('No Calcutta TVs connected.'); return }
    const ann = { text, postedAt: serverTimestamp() }
    const updates = {}; ids.forEach((id) => { updates[`screens/${id}/announcement`] = ann })
    update(tref('tvControl'), updates).catch(() => {})
    setMsg(`Announcement posted to ${annTarget === 'all' ? 'all Calcutta TVs' : (screens[annTarget]?.name || `TV ${annTarget}`)}.`)
  }
  const clearAnn = () => {
    const ids = annIds()
    const updates = {}; ids.forEach((id) => { updates[`screens/${id}/announcement`] = null })
    update(tref('tvControl'), updates).catch(() => {})
    setMsg('Announcement cleared.')
  }
  const tabs = [...CALCUTTA_TV_SCENES, { key: 'rotate', label: 'Rotate', icon: '🔁' }]
  return (
    <div className="card">
      <div className="no-print">
        <h2 className="text-lg font-bold">📺 Calcutta TV Screen Control</h2>
        <p className="text-xs text-gray-500 max-w-xl mt-1">
          Controls only the <b>Calcutta “C” pool</b> (screens opened at <span className="font-mono">/tv?screen=1C</span>, <span className="font-mono">2C</span>, …).
          Click a tab to put that Calcutta screen up on that TV. The tournament TV control never sees these. MGA never shows.
        </p>
      </div>

      {/* Announcements — scoped to the C pool */}
      <div className="mt-3 no-print rounded-2xl border-2 border-gray-200 p-3">
        <div className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-1">📢 Announcements</div>
        <textarea value={annText} onChange={(e) => setAnnText(e.target.value)} rows={2}
          placeholder="Message to scroll over the Calcutta TV(s) — e.g. “Championship flight up next.”"
          className="w-full rounded-lg border border-gray-300 p-2 text-sm" />
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <label className="flex items-center gap-1.5 text-xs text-gray-600">
            <span className="uppercase tracking-widest">Target</span>
            <select value={annTarget} onChange={(e) => setAnnTarget(e.target.value)} className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm font-bold">
              <option value="all">All Calcutta screens</option>
              {tvs.map(([id, s]) => <option key={id} value={id}>{s?.name || `TV ${id}`} (screen {id})</option>)}
            </select>
          </label>
          <button type="button" onClick={postAnn} disabled={!annText.trim()} className="px-4 py-2 rounded-lg font-extrabold text-sm text-white disabled:opacity-50" style={{ background: 'var(--ge-primary)' }}>📢 Post</button>
          <button type="button" onClick={clearAnn} className="px-4 py-2 rounded-lg font-bold text-sm border border-gray-300">✕ Clear</button>
          <button type="button" onClick={() => setShowEmoji((v) => !v)} className="px-3 py-2 rounded-lg font-bold text-sm border border-gray-300">😊 Emoji</button>
        </div>
        {showEmoji ? (
          <div className="mt-2 p-2 rounded-lg border border-gray-200 flex flex-wrap gap-1 bg-white">
            {CALCUTTA_ANN_EMOJIS.map((e) => (
              <button key={e} type="button" onClick={() => setAnnText((t) => t + e)} className="text-lg hover:bg-gray-100 rounded px-1">{e}</button>
            ))}
          </div>
        ) : null}
      </div>
      {msg ? <p className="text-xs text-gray-600 mt-2 no-print">{msg}</p> : null}

      {/* One control block per connected "C" TV */}
      {tvs.length === 0 ? (
        <div className="mt-3 rounded-2xl border-2 border-dashed border-gray-300 p-6 text-center no-print">
          <p className="font-bold">No Calcutta TVs connected yet</p>
          <p className="text-sm text-gray-500 mt-1">On each TV, open <span className="font-mono font-bold">/tv?screen=1C</span>, <span className="font-mono font-bold">/tv?screen=2C</span>, … (the <b>C</b> = Calcutta-only) and they'll appear here.</p>
        </div>
      ) : (
        <div className="mt-3 space-y-4 no-print">
          {tvs.map(([id, s]) => {
            const live = now - Number(s?.lastSeen || 0) < CAL_LIVE_WINDOW_MS
            const mode = s?.mode || 'calcutta-title'
            const speed = CAL_SPEEDS.includes(Number(s?.rotateSeconds)) ? Number(s.rotateSeconds) : 30
            return (
              <div key={id} className="rounded-2xl border-2 border-gray-200 p-4">
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  <span className="text-xl font-extrabold whitespace-nowrap">TV {id}</span>
                  <span className="text-xl font-extrabold text-gray-300">—</span>
                  <input
                    value={nameDraft[id] ?? (s?.name || '')}
                    onChange={(e) => { setNameDraft((d) => ({ ...d, [id]: e.target.value })); setName(id, e.target.value) }}
                    placeholder="Name this TV (e.g. Bar-C)"
                    title="Rename this Calcutta TV — panel label only"
                    className="text-lg font-bold bg-transparent border-b border-dashed border-gray-300 focus:outline-none focus:border-green-deep px-1"
                    style={{ minWidth: 130, maxWidth: 260 }} />
                  <span className="text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-full flex items-center gap-1.5"
                    style={{ backgroundColor: live ? 'rgba(22,163,74,0.15)' : 'rgba(0,0,0,0.06)', color: live ? '#15803D' : '#6B7280' }}>
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: live ? '#22C55E' : '#9CA3AF' }} />
                    {live ? 'Live' : 'Offline'}
                  </span>
                  <button onClick={() => removeScreen(id, s?.name)} title={`Remove TV ${id}`}
                    className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"
                    style={{ backgroundColor: 'rgba(177,12,12,0.12)', color: '#B10C0C', border: '1px solid rgba(177,12,12,0.35)' }}>
                    🗑 Remove
                  </button>
                </div>

                {/* Scene tabs — the six Calcutta screens + Rotate */}
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
                  {tabs.map((m) => {
                    const on = mode === m.key
                    return (
                      <button key={m.key} onClick={() => setMode(id, m.key)}
                        className="py-3 rounded-xl font-extrabold text-xs transition active:scale-[0.98]"
                        style={on ? { background: 'var(--ge-primary)', color: '#fff' } : { background: '#fff', color: 'var(--ge-primary)', border: '1px solid #d1d5db' }}>
                        <span className="block text-2xl leading-none mb-1">{m.icon}</span>
                        {m.label}
                      </button>
                    )
                  })}
                </div>

                {/* Rotate speed — only used in Rotate mode */}
                <div className="mt-3 flex items-center gap-2 flex-wrap" style={{ opacity: mode === 'rotate' ? 1 : 0.45 }}>
                  <span className="text-xs uppercase tracking-widest mr-1 text-gray-500">Rotate speed</span>
                  {CAL_SPEEDS.map((sp) => {
                    const on = speed === sp
                    return (
                      <button key={sp} onClick={() => setSpeed(id, sp)}
                        className="px-4 py-2 rounded-lg font-bold text-sm"
                        style={on ? { background: 'var(--ge-gold)', color: 'var(--ge-primary)' } : { background: '#fff', color: 'var(--ge-primary)', border: '1px solid #d1d5db' }}>
                        {sp}s
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Auction Runner — the auctioneer's console ──────────────────────────────
// Admin picks a flight (→ it becomes the public display), then for each team
// enters only Winning Bid + Owner and hits Save. Save writes the sale and auto-
// advances to the next team; the public display (CalcuttaLive) mirrors the live
// flight+team pointer instantly. Owner has autocomplete from owners already
// entered; any sold row is editable by tapping it in the list below.
function AuctionRunner({ tournamentId, flights, bids, live, finished, flightResult, mgaPct, payoutStructure, buyBackEnabled }) {
  const flightList = useMemo(() => flights.map((f) => ({
    id: f.id,
    name: f.info?.name || f.id,
    teams: Object.entries(f.teams || {}).map(([id, t]) => ({ id, name: lastNames(t.info?.name) || id, players: t.info?.players || [] })),
  })), [flights])

  const currentFlightId = (live?.flightId && flightList.some((f) => f.id === live.flightId)) ? live.flightId : (flightList[0]?.id || null)
  const flight = flightList.find((f) => f.id === currentFlightId) || null
  const teams = flight?.teams || []
  const currentTeamId = (live?.teamId && teams.some((t) => t.id === live.teamId)) ? live.teamId : (teams[0]?.id || null)
  const idx = teams.findIndex((t) => t.id === currentTeamId)
  const team = teams[idx] || null

  const owners = useMemo(
    () => [...new Set(Object.values(bids).map((b) => (b.buyerName || '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [bids],
  )

  const [bidAmount, setBidAmount] = useState('')
  const [owner, setOwner] = useState('')
  const [aiEntry, setAiEntry] = useState(false)
  const [aiTv, setAiTv] = useState(false)
  const [aiText, setAiText] = useState('')
  const [aiMsg, setAiMsg] = useState('')
  const pendingRef = useRef(null) // AI-parsed { teamId, bid, owner } applied after the on-block change
  // Load the current team's existing sale into the inputs (for corrections) — but
  // honor an AI-parsed pending value for the team we just jumped to.
  useEffect(() => {
    if (pendingRef.current && pendingRef.current.teamId === currentTeamId) {
      const p = pendingRef.current; pendingRef.current = null
      setBidAmount(p.bid != null ? String(p.bid) : '')
      setOwner(p.owner || '')
      return
    }
    const b = bids[currentTeamId]
    setBidAmount(b?.bidAmount ? String(b.bidAmount) : '')
    setOwner(b?.buyerName || '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTeamId])

  const soldCount = teams.filter((t) => Number(bids[t.id]?.bidAmount) > 0).length
  const allSold = teams.length > 0 && soldCount === teams.length
  const isFinished = !!finished?.[currentFlightId]?.done

  const fp = useMemo(() => {
    const arr = flights.map((f) => ({ id: f.id, ...f }))
    return computeFlightPayouts(arr, bids, { mgaPct, payoutStructure, buyBackEnabled }).find((x) => x.flightId === currentFlightId) || null
  }, [flights, bids, mgaPct, payoutStructure, buyBackEnabled, currentFlightId])

  const selectFlight = (fid) => {
    const f = flightList.find((x) => x.id === fid)
    setCalcuttaLive(tournamentId, { flightId: fid, teamId: f?.teams?.[0]?.id || null })
  }
  const goTo = (teamId) => setCalcuttaLive(tournamentId, { flightId: currentFlightId, teamId })

  const save = async () => {
    if (!currentTeamId) return
    await setCalcuttaBid(tournamentId, currentTeamId, { buyerName: owner.trim(), bidAmount: Number(bidAmount) || 0 })
    const next = teams[idx + 1]
    if (next) setCalcuttaLive(tournamentId, { flightId: currentFlightId, teamId: next.id })
  }

  // ── AI assist: parse a spoken/typed line → fill the runner fields (editable;
  // NOTHING commits until Save). "finish flight" finishes once all are sold. ──
  const applyLine = (text) => {
    const line = String(text || '').trim()
    if (!line) return
    if (isFinishCommand(line)) {
      if (allSold && !isFinished) { finishCalcuttaFlight(tournamentId, currentFlightId); setAiMsg('Finished the flight.') }
      else setAiMsg('Not all teams are sold yet — record the sales first (or finish manually).')
      return
    }
    const r = parseCalcuttaLine(line, teams)
    if (r.teamId && r.teamId !== currentTeamId) {
      pendingRef.current = { teamId: r.teamId, bid: r.bid, owner: r.owner }
      goTo(r.teamId)
    } else {
      if (r.bid != null) setBidAmount(String(r.bid))
      if (r.owner) setOwner(r.owner)
    }
    setAiMsg(`Heard → ${r.matchedName || '(team?)'} · ${r.owner || '(owner?)'} · ${r.bid != null ? `$${r.bid}` : '(bid?)'} — review & Save`)
  }
  const speech = useSpeech({ onResult: (t) => { setAiText(t); applyLine(t) } })

  // ── AI: TV screens — auto-drive the Calcutta "C" pool to follow the auction. ──
  const currentSold = Number(bids[currentTeamId]?.bidAmount) > 0
  useEffect(() => {
    if (!aiTv) return
    const scene = isFinished ? 'calcutta-results' : (team && !currentSold ? 'calcutta-spotlight' : 'calcutta-board')
    get(tref('tvControl/screens')).then((snap) => {
      const screens = snap.val() || {}
      const updates = {}
      Object.keys(screens).forEach((id) => { if (isCalcuttaScreen(id)) { updates[`screens/${id}/mode`] = scene; updates[`screens/${id}/tournamentId`] = tournamentId } })
      if (Object.keys(updates).length) update(tref('tvControl'), updates).catch(() => {})
    }).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiTv, isFinished, currentTeamId, currentFlightId, currentSold])

  if (flightList.length === 0) {
    return <div className="card text-sm text-gray-600">Add teams in <b>Flights &amp; Teams</b> to run the auction.</div>
  }
  const canSave = Number(bidAmount) > 0 && owner.trim().length > 0

  return (
    <div className="card">
      <h3 className="text-xs uppercase tracking-widest text-gray-500 font-bold">🎤 Run the Auction</h3>
      <p className="text-xs text-gray-500 mt-1">Pick a flight (it becomes the public display), then just enter the bid + owner and Save — it advances automatically.</p>

      {/* AI assist — two independent toggles + Take Back Over. Manual is the baseline. */}
      <div className="mt-3 rounded-xl border-2 border-gray-200 p-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-bold uppercase tracking-widest text-gray-500">🤖 AI Assist</span>
            <button type="button" onClick={() => setAiEntry((v) => !v)}
              className={`rounded-full px-3 py-1.5 text-sm font-bold border ${aiEntry ? 'bg-green-deep text-white border-green-deep' : 'bg-white text-gray-700 border-gray-300'}`}>
              {aiEntry ? '✓ ' : ''}AI: Auction entry
            </button>
            <button type="button" onClick={() => setAiTv((v) => !v)}
              className={`rounded-full px-3 py-1.5 text-sm font-bold border ${aiTv ? 'bg-green-deep text-white border-green-deep' : 'bg-white text-gray-700 border-gray-300'}`}>
              {aiTv ? '✓ ' : ''}AI: TV screens
            </button>
          </div>
          {(aiEntry || aiTv) ? (
            <button type="button" onClick={() => { setAiEntry(false); setAiTv(false); speech.stop() }}
              className="rounded-full px-3 py-1.5 text-sm font-extrabold text-white" style={{ background: '#B10C0C' }}>
              ✋ Take Back Over
            </button>
          ) : null}
        </div>
        {aiEntry ? (
          <>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <input value={aiText} onChange={(e) => setAiText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { applyLine(aiText); setAiText('') } }}
                placeholder='Speak or type: "Langford Gunter, Bob Anderson, forty-two hundred"'
                className="flex-1 min-w-[220px] border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              <button type="button" onClick={() => { applyLine(aiText); setAiText('') }} className="btn-primary text-sm px-3">Apply</button>
              {speech.supported ? (
                <button type="button" onClick={() => (speech.listening ? speech.stop() : speech.start())}
                  className="rounded-lg px-3 py-2 text-sm font-bold text-white" style={{ background: speech.listening ? '#B10C0C' : 'var(--ge-primary)' }}>
                  {speech.listening ? '● Listening…' : '🎤 Speak'}
                </button>
              ) : <span className="text-xs text-gray-400">Mic unsupported</span>}
            </div>
            {aiMsg ? <p className="text-xs text-gray-600 mt-1">{aiMsg}</p> : null}
            <p className="text-[11px] text-gray-400 mt-1">AI fills the fields below — nothing saves until you tap <b>Save &amp; Next</b>. Say “finish flight” once all are sold.</p>
          </>
        ) : null}
        {aiTv ? <p className="text-[11px] text-gray-500 mt-1">AI is driving the Calcutta “C” TVs to follow the auction (spotlight → board → results). Title / Up Next stay manual in the C control.</p> : null}
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        {flightList.map((f) => (
          <button
            key={f.id}
            onClick={() => selectFlight(f.id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-bold transition ${f.id === currentFlightId ? 'bg-green-deep text-white' : 'bg-white border border-gray-300 hover:bg-gray-50'}`}>
            {f.name}{finished?.[f.id]?.done ? ' ✓' : ''}
          </button>
        ))}
      </div>

      {team ? (
        <div className="mt-4 rounded-2xl border-2 border-green-deep p-4">
          <div className="text-[11px] uppercase tracking-widest font-bold text-green-deep">On the block · {idx + 1} of {teams.length}</div>
          <div className="text-2xl font-extrabold mt-0.5">{team.name}</div>
          {team.players.length ? <div className="text-xs text-gray-500 mt-0.5">{team.players.join(' · ')}</div> : null}

          <div className="grid grid-cols-2 gap-3 mt-4">
            <label className="block">
              <span className="text-xs font-bold text-gray-500 uppercase">Winning Bid</span>
              <input inputMode="numeric" pattern="[0-9]*" value={bidAmount}
                onChange={(e) => setBidAmount(e.target.value.replace(/[^0-9]/g, '').slice(0, 7))}
                placeholder="$" className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-lg font-bold" />
            </label>
            <label className="block">
              <span className="text-xs font-bold text-gray-500 uppercase">Owner</span>
              <input list="calcutta-owners" value={owner} onChange={(e) => setOwner(e.target.value)}
                placeholder="Name" autoCapitalize="words"
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-lg font-bold" />
              <datalist id="calcutta-owners">{owners.map((o) => <option key={o} value={o} />)}</datalist>
            </label>
          </div>

          <div className="flex items-center gap-2 mt-3">
            <button onClick={() => idx > 0 && goTo(teams[idx - 1].id)} disabled={idx <= 0} className="btn-ghost text-sm px-3">‹ Prev</button>
            <button onClick={save} disabled={!canSave} className="btn-primary flex-1">Save &amp; Next ›</button>
            <button onClick={() => idx < teams.length - 1 && goTo(teams[idx + 1].id)} disabled={idx >= teams.length - 1} className="btn-ghost text-sm px-3">Skip ›</button>
          </div>
        </div>
      ) : null}

      <div className="mt-4 divide-y divide-gray-100">
        {teams.map((t, i) => {
          const b = bids[t.id]
          const sold = Number(b?.bidAmount) > 0
          return (
            <button
              key={t.id}
              onClick={() => goTo(t.id)}
              className={`w-full flex items-center justify-between gap-3 py-2 px-2 text-left rounded ${t.id === currentTeamId ? 'bg-amber-50' : 'hover:bg-gray-50'}`}>
              <span className="text-sm font-semibold truncate">{i + 1}. {t.name}</span>
              <span className="text-sm whitespace-nowrap">{sold ? <><b>${b.bidAmount}</b> · {b.buyerName}</> : <span className="text-gray-400">—</span>}</span>
            </button>
          )
        })}
      </div>

      {fp ? (
        <div className="mt-4 rounded-xl bg-green-50 border border-green-200 p-3 text-sm">
          {/* Net payouts (already after the 10% MGA cut). MGA runs in the math but
              isn't shown as a labeled line. */}
          <div className="font-bold text-green-deep">{flight.name} pot: ${fp.pot.toLocaleString()}</div>
          <div className="mt-1">{fp.payouts.map((p) => <span key={p.place} className="mr-4">{ordinal(p.place)}: <b>${p.amount.toLocaleString()}</b></span>)}</div>
        </div>
      ) : null}

      {/* End-of-day result — the flight's 1st/2nd place teams (their owners win
          the payout). Shows on the /calcutta-live flight-result screen. */}
      <div className="mt-4 rounded-xl bg-gray-50 border border-gray-200 p-3">
        <div className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-2">End-of-day result (flight winners)</div>
        <div className="grid grid-cols-2 gap-2">
          {['first', 'second'].map((slot, i) => {
            const dr = flightResult?.[currentFlightId] || {}
            const val = slot === 'first' ? (dr.firstTeamId || '') : (dr.secondTeamId || '')
            const save = (v) => setCalcuttaFlightResult(tournamentId, currentFlightId, {
              firstTeamId: slot === 'first' ? (v || null) : (dr.firstTeamId || null),
              secondTeamId: slot === 'second' ? (v || null) : (dr.secondTeamId || null),
            })
            return (
              <label key={slot} className="block">
                <span className="text-xs text-gray-500">{i === 0 ? '🥇 1st place team' : '🥈 2nd place team'}</span>
                <select value={val} onChange={(e) => save(e.target.value)} className="mt-1 w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm">
                  <option value="">—</option>
                  {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </label>
            )
          })}
        </div>
      </div>

      <div className="flex items-center justify-between mt-3">
        <div className="text-xs text-gray-500">{soldCount}/{teams.length} sold</div>
        <button onClick={() => finishCalcuttaFlight(tournamentId, currentFlightId)} disabled={!allSold || isFinished} className="btn-primary">
          {isFinished ? 'Flight Finished ✓' : 'Finish Flight'}
        </button>
      </div>
    </div>
  )
}

// ── Section 1: Settings ────────────────────────────────────────────────────
function SettingsCard({ tournamentId, settings, payoutStructure }) {
  const [open, setOpen] = useState(false)
  const [rows, setRows] = useState(payoutStructure)
  const [buyBackEnabled, setBuyBackEnabled] = useState(!!settings.buyBackEnabled)
  const [buyBackPct, setBuyBackPct] = useState(Number(settings.buyBackPct) || 50)
  const [savedAt, setSavedAt] = useState(0)

  useEffect(() => { setRows(payoutStructure) }, [payoutStructure])
  useEffect(() => { setBuyBackEnabled(!!settings.buyBackEnabled) }, [settings.buyBackEnabled])
  useEffect(() => { setBuyBackPct(Number(settings.buyBackPct) || 50) }, [settings.buyBackPct])

  const total = rows.reduce((n, r) => n + (Number(r.pct) || 0), 0)
  const valid = total === 100

  const updateRow = (i, patch) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))
  const addRow = () =>
    setRows((rs) => [...rs, { place: rs.length + 1, pct: 0 }])
  const removeRow = (i) =>
    setRows((rs) => rs.filter((_, idx) => idx !== i).map((r, idx) => ({ ...r, place: idx + 1 })))

  const save = async () => {
    if (!valid) return
    const cleaned = rows
      .map((r, i) => ({ place: i + 1, pct: Number(r.pct) || 0 }))
      .filter((r) => r.pct > 0)
    await setCalcuttaSettings(tournamentId, {
      enabled: true,
      payoutStructure: cleaned,
      buyBackEnabled,
      buyBackPct: Number(buyBackPct) || 0,
    })
    setSavedAt(Date.now())
  }

  const disable = async () => {
    if (!confirm('Disable Calcutta? Bids and settings stay saved — you can re-enable later.')) return
    await setCalcuttaSettings(tournamentId, { enabled: false })
  }

  return (
    <div className="card">
      <button
        className="w-full flex justify-between items-center text-left"
        onClick={() => setOpen((o) => !o)}>
        <div>
          <h2 className="text-lg font-bold">⚙️ Settings</h2>
          <p className="text-xs text-gray-500">
            {PBCC_PREMIUM
              ? `Per flight: MGA ${Number.isFinite(Number(settings.mgaPct)) ? Number(settings.mgaPct) : DEFAULT_MGA_PCT}% off the top, then ${payoutStructure.map((r) => `${r.pct}%`).join(' / ')} to ${payoutStructure.map((r) => ordinal(r.place)).join(' / ')}`
              : `Payout: ${payoutStructure.map((r) => `${r.pct}%`).join(' / ')}`} · Buy-back: {settings.buyBackEnabled ? `${settings.buyBackPct || 0}%` : 'off'}
          </p>
        </div>
        <span className="text-gray-400">{open ? '▾' : '▸'}</span>
      </button>

      {open ? (
        <div className="mt-4 space-y-4">
          <div>
            <div className="flex justify-between items-center">
              <h3 className="font-bold text-sm uppercase tracking-widest text-gray-500">Payout Structure</h3>
              <button className="btn-ghost text-xs" onClick={addRow}>+ Add Place</button>
            </div>
            <p className={`text-xs mt-1 ${valid ? 'text-green-deep' : 'text-red-600'}`}>
              Total: <b>{total}%</b> {valid ? '✓' : '— must equal 100%'}
            </p>
            <div className="mt-2 space-y-1">
              {rows.map((r, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-xs uppercase tracking-widest text-gray-500 font-bold w-16">
                    {ordinal(i + 1)}
                  </span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    className="input w-24"
                    value={r.pct}
                    onChange={(e) => updateRow(i, { pct: Number(e.target.value) || 0 })}
                  />
                  <span className="text-sm text-gray-500">%</span>
                  {rows.length > 1 ? (
                    <button
                      className="text-red-600 text-xs ml-auto"
                      onClick={() => removeRow(i)}>Remove</button>
                  ) : null}
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-bold text-sm uppercase tracking-widest text-gray-500">Buy-Back</h3>
            <label className="inline-flex items-center gap-2 text-sm mt-2">
              <input
                type="checkbox"
                checked={buyBackEnabled}
                onChange={(e) => setBuyBackEnabled(e.target.checked)}
              />
              Allow teams to buy themselves back
            </label>
            {buyBackEnabled ? (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs">Buy-back %:</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="input w-24"
                  value={buyBackPct}
                  onChange={(e) => setBuyBackPct(Number(e.target.value) || 0)}
                />
                <span className="text-xs text-gray-500">
                  (cost = bid × {buyBackPct}% ÷ 2)
                </span>
              </div>
            ) : null}
          </div>

          <div className="flex justify-between items-center">
            <button className="text-red-600 text-xs font-semibold" onClick={disable}>
              Disable Calcutta
            </button>
            <div className="flex items-center gap-3">
              {savedAt ? (
                <span className="text-xs text-green-deep">Saved · {new Date(savedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
              ) : null}
              <button className="btn-primary" disabled={!valid} onClick={save}>Save Settings</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

// ── Section 2: Auction QR Code + Status Toggle ─────────────────────────────
//
// Print output is a 2×3 grid (six identical copies on one sheet) so admins
// can tape copies to every table, the bar, the door, etc. Everyone scans
// simultaneously instead of crowding around one screen.
function AuctionQRCard({ tournamentId, auctionOpen }) {
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const url = `${origin}/calcutta-bid?t=${tournamentId}`
  const hostLabel = (typeof window !== 'undefined' && window.location?.host) || ''
  // Admin-driven copy count for the room-copies print sheet. No max — they
  // type whatever they need; the 2-column grid flows across as many pages
  // as required (the browser handles page breaks via breakInside: 'avoid').
  const [copies, setCopies] = useState(8)
  return (
    <div className="card">
      <div className="flex flex-wrap justify-between items-start gap-3 no-print">
        <div>
          <h2 className="text-lg font-bold">📱 Auction QR Code</h2>
          <p className="text-xs text-gray-500 max-w-md mt-1">
            One QR for the entire auction. All bidders scan this code on their phones to enter their name and place bids.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`pill ${auctionOpen ? 'pill-green' : 'pill-gray'} text-xs`}>
            {auctionOpen ? '🟢 Open for Bidding' : '🔒 Closed'}
          </span>
          <button
            className="btn-secondary text-xs"
            onClick={() => setAuctionOpen(tournamentId, !auctionOpen)}>
            {auctionOpen ? 'Close Auction' : 'Open Auction'}
          </button>
          <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 font-bold">
            Copies:
            <input
              type="number"
              min="1"
              className="input w-16 text-center text-sm py-1"
              value={copies}
              onChange={(e) => setCopies(Math.max(1, Number(e.target.value) || 1))}
            />
          </label>
          <button className="btn-primary" onClick={() => window.print()}>🖨️ Print Room Copies</button>
        </div>
      </div>

      <div className="mt-4 bg-white border-4 border-dashed border-green-deep rounded-3xl p-6 text-center max-w-lg mx-auto no-print">
        <div className="text-xs uppercase tracking-widest text-green-deep font-bold">⛳ Calcutta Auction</div>
        <div className="text-2xl font-extrabold mt-1 text-green-deep">SCAN TO PLACE BIDS</div>
        <p className="text-sm text-gray-600 mt-2 leading-snug px-3">
          All bidders scan this one code. Enter your name, then bid on teams from your phone.
        </p>
        <div className="my-5 flex justify-center">
          <QRCodeCanvas value={url} size={260} includeMargin />
        </div>
        <div className="text-[11px] text-gray-400 break-all font-mono">{url}</div>
      </div>

      <p className="mt-3 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 max-w-lg mx-auto no-print">
        💡 <b>Print multiple copies and place them around the room</b> — everyone scans at the same time and bids live on their own phone.
      </p>

      {/* Print-only 2-column sheet of identical QR copies. Hidden on screen.
         Flows across multiple pages — admins choose the copy count above. */}
      <div className="hidden print:grid grid-cols-2 gap-3 p-2">
        {Array.from({ length: Math.max(1, Number(copies) || 1) }).map((_, i) => (
          <RoomCopyCard key={i} url={url} host={hostLabel} />
        ))}
      </div>
    </div>
  )
}

function RoomCopyCard({ url, host }) {
  return (
    <div
      className="border-4 border-dashed border-green-deep rounded-2xl p-3 text-center"
      style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
      <div className="text-[10px] uppercase tracking-widest text-green-deep font-extrabold">⛳ Calcutta Auction</div>
      <div className="text-base font-extrabold mt-0.5 text-green-deep leading-tight">Scan to bid on your phone</div>
      <div className="my-2 flex justify-center">
        <QRCodeCanvas value={url} size={180} includeMargin />
      </div>
      <div className="text-[10px] text-gray-600 font-mono break-all">{host || url}</div>
    </div>
  )
}

// ── Section 3: Live Bid Board ──────────────────────────────────────────────
function LiveBidBoardCard({ tournamentId, allTeams, bids, buyBackEnabled }) {
  const [editing, setEditing] = useState(null) // { teamId, teamName }
  const [open, setOpen] = useState(false) // the 104-row board is DEFAULT COLLAPSED

  if (allTeams.length === 0) {
    return (
      <div className="card text-center py-6 text-sm text-gray-500">
        Add teams via <b>Flights & Teams</b> before opening the auction.
      </div>
    )
  }

  const bidCount = Object.keys(bids).length
  return (
    <div className="card">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex flex-wrap justify-between items-center gap-2 text-left">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-gray-400 text-lg leading-none">{open ? '▾' : '▸'}</span>
          <div>
            <h2 className="text-lg font-bold">📊 Live Bid Board</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {open ? 'Updates in real-time as bidders submit from their phones.' : 'Tap to expand the full team-by-team board.'}
            </p>
          </div>
        </div>
        <div className="text-xs text-gray-600 font-semibold">
          {bidCount}/{allTeams.length} team{allTeams.length === 1 ? '' : 's'} sold
        </div>
      </button>
      {open ? (
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-gray-500 border-b">
            <tr>
              <th className="text-left py-2">Team</th>
              <th className="text-left py-2">Flight</th>
              <th className="text-right py-2">Current Bid</th>
              <th className="text-left py-2">Bidder</th>
              {buyBackEnabled ? <th className="text-center py-2">BB</th> : null}
              <th className="text-right py-2"></th>
            </tr>
          </thead>
          <tbody>
            {allTeams.map((t) => {
              const b = bids[t.teamId]
              return (
                <tr key={`${t.flightId}-${t.teamId}`} className="border-b last:border-0">
                  <td className="py-2 font-semibold">{t.teamName}</td>
                  <td className="py-2 text-xs text-gray-500">{t.flightName}</td>
                  <td className="py-2 text-right font-bold">
                    {b?.bidAmount ? `$${Number(b.bidAmount).toLocaleString()}` : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="py-2">
                    {b?.buyerName || <span className="text-gray-300">—</span>}
                  </td>
                  {buyBackEnabled ? (
                    <td className="py-2 text-center text-xs">
                      {b?.buyBack ? <span className="text-green-deep">✓ +${Number(b.buyBackCost || 0).toLocaleString()}</span> : ''}
                    </td>
                  ) : null}
                  <td className="py-2 text-right">
                    <button
                      className="text-xs text-gray-600 hover:text-green-deep font-semibold"
                      onClick={() => setEditing(t)}
                      title="Override or clear this bid">
                      ✏️
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      ) : null}
      {editing ? (
        <EditBidModal
          tournamentId={tournamentId}
          team={editing}
          bid={bids[editing.teamId] || {}}
          buyBackEnabled={buyBackEnabled}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  )
}

function EditBidModal({ tournamentId, team, bid, buyBackEnabled, onClose }) {
  const [name, setName] = useState(bid.buyerName || '')
  const [amount, setAmount] = useState(bid.bidAmount || '')
  const [buyBack, setBuyBack] = useState(!!bid.buyBack)

  const save = async (e) => {
    e?.preventDefault()
    await setCalcuttaBid(tournamentId, team.teamId, {
      buyerName: name,
      bidAmount: Number(amount) || 0,
      buyBack,
      buyBackCost: bid.buyBackCost || 0, // recomputed by recalcBuyBack below if changed
    })
    onClose()
  }

  const clear = async () => {
    if (!confirm(`Clear the bid for "${team.teamName}"?`)) return
    await clearCalcuttaBid(tournamentId, team.teamId)
    onClose()
  }

  return (
    <Modal title={`Override bid · ${team.teamName}`} onClose={onClose}>
      <p className="text-xs text-gray-500 -mt-1 mb-3">
        Manual override. Use this only to fix a write-in or remove a bad bid.
      </p>
      <form onSubmit={save} className="space-y-3">
        <Field label="Buyer name">
          <input autoFocus className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Bid amount">
          <input type="number" min="0" className="input" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        {buyBackEnabled ? (
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={buyBack} onChange={(e) => setBuyBack(e.target.checked)} />
            Buy-back active
          </label>
        ) : null}
        <div className="flex justify-between items-center pt-2">
          <button type="button" className="text-red-600 text-xs font-semibold" onClick={clear}>
            Clear bid
          </button>
          <div className="flex gap-2">
            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn-primary">Save</button>
          </div>
        </div>
      </form>
    </Modal>
  )
}

// ── Section 4: Pot Summary ─────────────────────────────────────────────────
// TEST: per-flight payout summary — each flight pays 1st/2nd from ITS own pot
// after MGA takes its cut off the top.
function FlightPayoutsCard({ flightPayouts, mgaPct, totalPot, bidCount }) {
  const rows = (flightPayouts || []).filter((f) => f.pot > 0)
  const mgaTotal = (flightPayouts || []).reduce((s, f) => s + f.mgaCut, 0)
  const places = (flightPayouts?.[0]?.payouts) || [{ place: 1, pct: 70 }, { place: 2, pct: 30 }]
  return (
    <div className="card">
      <h2 className="text-lg font-bold">💰 Per-Flight Payouts</h2>
      <p className="text-sm text-gray-600 mt-1">
        Each flight pays from its OWN pot: MGA takes {mgaPct}% off the top, then the rest goes{' '}
        {places.map((p) => `${p.pct}% to ${ordinal(p.place)}`).join(', ')}.
      </p>
      <div className="mt-3 grid grid-cols-3 gap-3 text-center">
        <Stat label="Total Pot" value={`$${totalPot.toLocaleString()}`} sub={`${bidCount} team${bidCount === 1 ? '' : 's'} sold`} />
        <Stat label={`MGA (${mgaPct}%)`} value={`$${mgaTotal.toLocaleString()}`} sub="off the top" />
        <Stat label="Flights paying" value={String(rows.length)} sub="with bids" />
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500 mt-4">No bids yet — per-flight pots appear as teams are sold.</p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-1.5 pr-2">Flight</th>
                <th className="py-1.5 px-2 text-right">Pot</th>
                <th className="py-1.5 px-2 text-right">− MGA</th>
                {places.map((p) => <th key={p.place} className="py-1.5 px-2 text-right">{ordinal(p.place)} ({p.pct}%)</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((f) => (
                <tr key={f.flightId} className="border-b last:border-0">
                  <td className="py-1.5 pr-2 font-semibold">{f.flightName}</td>
                  <td className="py-1.5 px-2 text-right">${f.pot.toLocaleString()}</td>
                  <td className="py-1.5 px-2 text-right text-gray-500">−${f.mgaCut.toLocaleString()}</td>
                  {f.payouts.map((p) => <td key={p.place} className="py-1.5 px-2 text-right font-bold text-green-deep">${p.amount.toLocaleString()}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function Stat({ label, value, sub }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">{label}</div>
      <div className="text-2xl font-extrabold text-green-deep">{value}</div>
      {sub ? <div className="text-xs text-gray-500 mt-0.5">{sub}</div> : null}
    </div>
  )
}

function PotSummaryCard({ totalPot, payouts, bidCount }) {
  return (
    <div className="card">
      <h2 className="text-lg font-bold">💰 Pot Summary</h2>
      <div className="mt-3 grid sm:grid-cols-2 gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">Total Pot</div>
          <div className="text-3xl font-extrabold text-green-deep">${totalPot.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">{bidCount} team{bidCount === 1 ? '' : 's'} sold</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-1">Payouts</div>
          <ul className="space-y-1 text-sm">
            {payouts.map((p) => (
              <li key={p.place} className="flex justify-between">
                <span>{ordinal(p.place)} ({p.pct}%)</span>
                <span className="font-bold">${p.amount.toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

// ── Section 5: Payment Tracking ────────────────────────────────────────────
function PaymentTrackingCard({ tournamentId, buyers }) {
  if (buyers.length === 0) {
    return (
      <div className="card">
        <h2 className="text-lg font-bold">💵 Payment Tracking</h2>
        <p className="text-sm text-gray-500 mt-1">Buyers will appear here once bids come in.</p>
      </div>
    )
  }
  const totalOwed = buyers.reduce((n, b) => n + (b.paid ? 0 : b.totalOwed), 0)
  const totalCollected = buyers.reduce((n, b) => n + (b.paid ? b.totalOwed : 0), 0)
  return (
    <div className="card">
      <div className="flex flex-wrap justify-between items-end gap-2">
        <h2 className="text-lg font-bold">💵 Payment Tracking</h2>
        <div className="text-xs text-gray-600">
          Collected: <b className="text-green-deep">${totalCollected.toLocaleString()}</b> · Outstanding: <b className="text-amber-700">${totalOwed.toLocaleString()}</b>
        </div>
      </div>
      <div className="mt-3 divide-y divide-gray-100">
        {buyers.map((b) => (
          <div key={b.buyerKey} className={`py-3 ${b.paid ? 'opacity-60' : ''}`}>
            <div className="flex justify-between items-center gap-2">
              <div className="min-w-0">
                <div className="font-semibold truncate">{b.buyerName}</div>
                <div className="text-xs text-gray-500">
                  {b.teams.length} team{b.teams.length === 1 ? '' : 's'} · ${b.totalOwed.toLocaleString()}
                </div>
              </div>
              <button
                className={b.paid ? 'btn-ghost text-xs' : 'btn-primary text-xs'}
                onClick={() => setBuyerPaid(tournamentId, b.buyerKey, !b.paid)}>
                {b.paid ? '↺ Mark Unpaid' : '✓ Mark Paid'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Section 6: Buyer QR Codes (post-auction summary) ──────────────────────
function BuyerQRCard({ tournamentId, buyers }) {
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  if (buyers.length === 0) return null
  return (
    <div className="card">
      <div className="flex flex-wrap justify-between items-center gap-2 no-print">
        <div>
          <h2 className="text-lg font-bold">📱 Per-Buyer Summary QRs</h2>
          <p className="text-xs text-gray-500">
            One QR per buyer — direct link to their personal Calcutta summary (teams owned, total owed).
          </p>
        </div>
        <button className="btn-primary" onClick={() => window.print()}>🖨 Print all</button>
      </div>
      <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {buyers.map((b) => {
          const url = `${origin}/calcutta?t=${tournamentId}&buyer=${encodeURIComponent(b.buyerKey)}`
          return (
            <div key={b.buyerKey} className="bg-white border-2 border-dashed border-gray-300 rounded-2xl p-4 text-center">
              <div className="text-xs uppercase tracking-wider text-green-deep font-bold">Calcutta Summary</div>
              <div className="text-base font-extrabold mt-1">{b.buyerName}</div>
              <div className="text-xs text-gray-500">{b.teams.length} team{b.teams.length === 1 ? '' : 's'} · ${b.totalOwed.toLocaleString()}</div>
              <div className="my-3 flex justify-center">
                <QRCodeCanvas value={url} size={150} includeMargin />
              </div>
              <div className="text-[10px] text-gray-400 break-all">{url}</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return n + (s[(v - 20) % 10] || s[v] || s[0])
}
